import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import type { SvgIconComponent } from '@mui/icons-material'

import { HOLIDAYS_RADIUS } from '@/src/utils/holidays'

export interface StayOptionView {
    label: string
    price: string
    Icon: SvgIconComponent
}

interface StayOptionsProps {
    options: StayOptionView[]
    caption?: string
    isMobile: boolean
}

/**
 * A price list read as cards rather than as a six-column table. The table forced every occupancy
 * label to wrap over three lines to fit, which buried the one thing being compared — the price —
 * under the description of who it applies to. Here the price leads and the label explains it.
 */
const StayOptions: React.FC<StayOptionsProps> = ({ options, caption, isMobile }) => {
    return (
        <Stack spacing={1.5}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: isMobile ? 1 : 1.5
                }}
            >
                {options.map(({ label, price, Icon }) => (
                    <Stack
                        key={label}
                        spacing={1}
                        sx={{
                            p: isMobile ? 1.5 : 2,
                            borderRadius: HOLIDAYS_RADIUS.NESTED,
                            border: '1px solid',
                            borderColor: 'borders.default',
                            bgcolor: 'neutral.100'
                        }}
                    >
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'background.paper',
                                color: 'primary.main'
                            }}
                        >
                            <Icon sx={{ fontSize: 18 }} />
                        </Box>

                        <Typography
                            sx={{
                                fontSize: isMobile ? '1rem' : '1.125rem',
                                fontWeight: 700,
                                lineHeight: 1.25,
                                color: 'textColors.primary'
                            }}
                        >
                            {price}
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: isMobile ? '0.75rem' : '1rem',
                                lineHeight: 1.5,
                                color: 'textColors.subheading'
                            }}
                        >
                            {label}
                        </Typography>
                    </Stack>
                ))}
            </Box>

            {caption && <Typography sx={{ fontSize: '0.75rem', color: 'textColors.subheading' }}>{caption}</Typography>}
        </Stack>
    )
}

export { StayOptions }
export default StayOptions
